import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchLatestPermissions } from 'slices/auth/login/thunk';

/**
 * Custom hook to fetch and access user permissions
 * 
 * @param appName - Optional. The application name (e.g., "Masters", "Order", "Inventory")
 * @param moduleName - Optional. The module name (e.g., "Category", "Item", "Enquiry")
 * @returns Object containing permission flags and the raw permissions object.
 *          If appName and moduleName are provided, returns permissions for that specific app/module.
 *          If not provided, returns all user permissions.
 * 
 * @example
 * // Get permissions for a specific app and module
 * const { canView, canCreate, canUpdate, canDelete, permissions } = usePermission("Masters", "Category");
 * 
 * // Get all user permissions
 * const { allPermissions } = usePermission();
 * 
 * if (canView) {
 *   // Show view button
 * }
 */
export const usePermission = (appName?: string, moduleName?: string) => {
  const dispatch = useDispatch<any>();
  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];

  // Fetch permissions on mount (only once)
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
      }
    };
    fetchPermissions();
  }, [dispatch]);

  // If appName and moduleName are not provided, return all permissions
  if (!appName || !moduleName) {
    return {
      // All user permissions
      allPermissions: user_permissions,
      
      // Helper function to check permission for a specific app/module/action
      hasPermission: (app: string, module: string, action: string): boolean => {
        const app_permissions = user_permissions.find(
          (item: any) => item?.apps_name?.toLowerCase() === app?.toLowerCase()
        );
        
        if (!app_permissions) return false;
        
        const module_permissions = app_permissions?.modules_permissions?.find(
          (item: any) => item?.modules_name?.toLowerCase() === module?.toLowerCase()
        );
        
        if (!module_permissions) return false;
        
        const permissions = module_permissions?.permissions || {};
        const actionLower = action.toLowerCase();
        
        // Try exact match first
        if (permissions[action] === true) return true;
        // Then try case-insensitive match
        for (const [key, value] of Object.entries(permissions)) {
          if (key.toLowerCase() === actionLower && value === true) {
            return true;
          }
        }
        return false;
      },
    };
  }

  // Find app permissions (case-insensitive)
  const app_permissions = user_permissions.find(
    (item: any) => item?.apps_name?.toLowerCase() === appName?.toLowerCase()
  );

  // Find module permissions (case-insensitive)
  const module_permissions = app_permissions?.modules_permissions?.find(
    (item: any) => item?.modules_name?.toLowerCase() === moduleName?.toLowerCase()
  );

  // Get permissions object (default to empty object)
  const permissions = module_permissions?.permissions || {};

  // Extract permission flags (case-insensitive for action names)
  const getPermission = (action: string): boolean => {
    const actionLower = action.toLowerCase();
    // Try exact match first
    if (permissions[action] === true) return true;
    // Then try case-insensitive match
    for (const [key, value] of Object.entries(permissions)) {
      if (key.toLowerCase() === actionLower && value === true) {
        return true;
      }
    }
    return false;
  };

  return {
    // Permission flags (case-insensitive)
    canView: getPermission('View') || getPermission('view'),
    canCreate: getPermission('Create') || getPermission('create'),
    canUpdate: getPermission('Update') || getPermission('update'),
    canDelete: getPermission('Delete') || getPermission('delete'),
    
    // Raw permissions object for custom checks
    permissions,
    
    // Helper function for custom permission checks
    hasPermission: (action: string) => getPermission(action),
    
    // Check if any permissions exist for this module
    hasAnyPermission: Object.keys(permissions).length > 0,
  };
};

export default usePermission;

